import React, { useEffect, useState } from "react";
import {
    Button,
    TextField,
    MenuItem,
    Checkbox,
    Card,
    CardContent,
    CardHeader,
    Typography,
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
    Box,
} from "@mui/material";
import { Add } from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import api from "../../api/api";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import en_ja from "../../translations/en_ja";

const AddProduct = ({
    dialogOpen,
    onClose,
    setSnackbarType,
    setSnackbarOpen,
    setSnackbarMessage,
    onSubmit,
    editMode = false,
    productData
}) => {
    const userDetails = JSON.parse(localStorage.getItem("user"));

    const [formData, setFormData] = useState({
        title: "",
        description: "",
        product_code: "",
        product_price: "",
        view_type: "",
        status: false,
        uploaded_by: userDetails?.id || 1,
        product_images: [],
        product_video: [],
        product_manual: [],
        primary_image: "",
        questions_answers: []
    });

    const [primaryImage, setPrimaryImage] = useState(null);
    const [images, setImages] = useState([]);
    const [videos, setVideos] = useState([]);
    const [manuals, setManuals] = useState([]);

    const [imagePreviews, setImagePreviews] = useState([]);
    const [videoPreviews, setVideoPreviews] = useState([]);

    const [faqs, setFaqs] = useState([]);
    const [faqQuestion, setFaqQuestion] = useState("");
    const [faqAnswer, setFaqAnswer] = useState("");

    const [errors, setErrors] = useState({
        title: "",
        description: "",
        product_code: "",
        product_price: "",
    });

    const validateForm = () => {
        const newErrors = {};
        if (!(formData.title || "").trim()) newErrors.title = en_ja["Product name is required"];
        if (!(formData.description || "").trim()) newErrors.description = en_ja["Description is required"];
        if (!(formData.product_code || "").trim()) newErrors.product_code = en_ja["Product code is required"];
        if (!(String(formData.product_price || "").trim())) {
            newErrors.product_price = en_ja["Price is required"];
        } else if (isNaN(formData.product_price)) {
            newErrors.product_price = en_ja["Price must be a number"];
        }
        if (!formData.view_type) newErrors.view_type = en_ja["View type is required"];

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    useEffect(() => {
        if (dialogOpen) {
            if (editMode && productData) {
                setFormData({
                    title: productData.title || "",
                    description: productData.description || "",
                    product_code: productData.product_code || "",
                    product_price: productData.product_price || "",
                    view_type: productData.view_type || "",
                    status: productData.status || false,
                    uploaded_by: productData.uploaded_by || userDetails?.id || 1,
                });
                setFaqs(productData.questions_answers || []);
                setPrimaryImage(productData.primary_image || null);
                setImagePreviews(Array.isArray(productData.product_images) ? productData.product_images : [productData.product_images].filter(Boolean));
                setVideoPreviews(Array.isArray(productData.product_video) ? productData.product_video : [productData.product_video].filter(Boolean));
                setManuals(Array.isArray(productData.product_manual) ? productData.product_manual : [productData.product_manual].filter(Boolean));
            } else {
                resetForm();
            }
        }
    }, [dialogOpen, editMode, productData]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value,
        }));
        setErrors((prev) => ({ ...prev, [name]: "" }));
    };

    const handleFileChange = (e, type) => {
        const files = Array.from(e.target.files);

        if (type === "primary") setPrimaryImage(files[0]);

        if (type === "images") {
            setImages((prev) => [...prev, ...files]);
            setImagePreviews((prev) => [
                ...prev,
                ...files.map((f) => (f instanceof File ? URL.createObjectURL(f) : f)),
            ]);
        }

        if (type === "videos") {
            setVideos((prev) => [...prev, ...files]);
            setVideoPreviews((prev) => [
                ...prev,
                ...files.map((f) => (f instanceof File ? URL.createObjectURL(f) : f)),
            ]);
        }

        if (type === "manuals") {
            setManuals((prev) => [...prev, ...files]);
        }
    };

    const handleAddFaq = () => {
        if (!faqQuestion || !faqAnswer) return;
        setFaqs([...faqs, { question: faqQuestion, answer: faqAnswer }]);
        setFaqQuestion("");
        setFaqAnswer("");
    };

    const handleDeleteFaq = (i) => setFaqs(faqs.filter((_, index) => index !== i));

    const handleEditFaq = (i) => {
        setFaqQuestion(faqs[i].question);
        setFaqAnswer(faqs[i].answer);
        setFaqs(faqs.filter((_, index) => index !== i));
    };

    const resetForm = () => {
        setFormData({
            title: "",
            description: "",
            product_code: "",
            product_price: "",
            view_type: "",
            status: false,
            uploaded_by: userDetails?.id || 1,
        });
        setPrimaryImage(null);
        setImages([]);
        setVideos([]);
        setManuals([]);
        setImagePreviews([]);
        setVideoPreviews([]);
        setFaqs([]);
        setFaqQuestion("");
        setFaqAnswer("");
        setErrors({});
    };

    const handleCloseDialog = () => {
        if (!editMode) resetForm();
        onClose();
    };

    const handleSubmit = async () => {
        if (!validateForm()) return;

        const fd = new FormData();
        fd.append("title", formData.title);
        fd.append("description", formData.description);
        fd.append("product_code", formData.product_code);
        fd.append("product_price", formData.product_price);
        fd.append("view_type", formData.view_type);
        // Warranty (offline) -> selectable true, Shop (online) -> selectable false. Payload only.
        fd.append("is_selectable", formData.view_type === "offline");
        fd.append("status", formData.status);
        fd.append("uploaded_by", formData.uploaded_by);

        fd.append("questions_answers", JSON.stringify(faqs));

        if (primaryImage instanceof File) {
            fd.append("primary_image", primaryImage);
        } else if (typeof primaryImage === "string") {
            fd.append("primary_image", primaryImage);
        }

        imagePreviews.forEach((item) => {
            if (typeof item === "string" && !item.startsWith("blob:")) {
                fd.append("product_images", item);
            }
        });
        images.forEach((file) => {
            fd.append("product_images", file);
        });
        videoPreviews.forEach((item) => {
            if (typeof item === "string" && !item.startsWith("blob:")) {
                fd.append("product_video", item);
            }
        });
        videos.forEach((file) => {
            fd.append("product_video", file);
        });
        manuals.forEach((item) => {
            if (item instanceof File) {
                fd.append("product_manual", item);
            } else if (typeof item === "string" && !item.startsWith("blob:")) {
                fd.append("product_manual", item);
            }
        });

        try {
            if (productData?.id) {
                await api.put(`/v1/admin/edit/product/${productData.id}`, fd, {
                    headers: { "Content-Type": "multipart/form-data" },
                });
                setSnackbarMessage(en_ja["Product updated successfully"]);
            } else {
                await api.post("/v1/admin/add/product", fd, {
                    headers: { "Content-Type": "multipart/form-data" },
                });
                setSnackbarMessage(en_ja["Product added successfully"]);
            }

            setSnackbarType("success");
            setSnackbarOpen(true);
            handleCloseDialog();
            if (onSubmit) onSubmit();
        } catch (err) {
            setSnackbarMessage(en_ja["Failed to save product"]);
            setSnackbarType("error");
            setSnackbarOpen(true);
            console.error(err);
        }
    };

    const getManualName = (item) => {
        if (item instanceof File) return item.name;
        if (typeof item === "string") return item.split("/").pop();
        return "";
    };

    return (
        <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="lg" fullWidth>
            <DialogTitle>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Typography variant="h6">{en_ja["Add Product"]}</Typography>
                    <IconButton onClick={handleCloseDialog}><CloseIcon /></IconButton>
                </Box>
            </DialogTitle>

            <DialogContent>
                <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                    {/* Left Column */}
                    <Box sx={{ flex: 1, minWidth: 300 }}>
                        <Card sx={{ mb: 2 }}>
                            <CardHeader title={en_ja["Basic Details"]} sx={{ pb: 0 }}
                                titleTypographyProps={{ variant: "subtitle1", sx: { fontWeight: 600 } }} />
                            <CardContent sx={{ pt: 0 }}>
                                <TextField
                                    label={en_ja["Product Name"]} name="title" fullWidth value={formData.title}
                                    onChange={handleChange} margin="normal" error={!!errors.title} helperText={errors.title}
                                />
                                <TextField
                                    label={en_ja["Description"]} name="description" fullWidth multiline minRows={4}
                                    value={formData.description} onChange={handleChange} margin="normal"
                                    error={!!errors.description} helperText={errors.description}
                                />
                                <TextField
                                    label={en_ja["Product Code"]} name="product_code" fullWidth value={formData.product_code}
                                    onChange={handleChange} margin="normal" error={!!errors.product_code}
                                    helperText={errors.product_code}
                                />
                                <TextField
                                    label={en_ja["Price"]} name="product_price" fullWidth value={formData.product_price}
                                    onChange={handleChange} margin="normal" InputProps={{ startAdornment: <span>¥ </span> }}
                                    error={!!errors.product_price} helperText={errors.product_price}
                                />
                                <TextField
                                    select
                                    label={en_ja["View Type"]} name="view_type" fullWidth value={formData.view_type}
                                    onChange={handleChange} margin="normal"
                                    error={!!errors.view_type} helperText={errors.view_type}
                                >
                                    <MenuItem value="offline">{en_ja["Warranty"]}</MenuItem>
                                    <MenuItem value="online">{en_ja["Shop"]}</MenuItem>
                                </TextField>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0 }}>
                                    <Checkbox name="status" checked={formData.status} onChange={handleChange} />
                                    <Typography variant="body2">{en_ja["Highlight this product in a featured section."]}</Typography>
                                </Box>
                            </CardContent>
                        </Card>
                    </Box>

                    {/* Right Column */}
                    
                    <Box sx={{ flex: 1, minWidth: 300 }}>
                        {/* Primary Image */}
                        <Card sx={{ mb: 2 }}>
                            <CardHeader title={en_ja["Primary Image"]} sx={{ pb: 0 }}
                                titleTypographyProps={{ variant: "subtitle1", sx: { fontWeight: 600 } }} />
                            <CardContent sx={{ pb: 0 }}>
                                <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
                                    {primaryImage && (
                                        <img
                                            src={primaryImage instanceof File ? URL.createObjectURL(primaryImage) : primaryImage}
                                            alt="primary" width={100} height={80} style={{ borderRadius: 4 }}
                                        />
                                    )}
                                    <Box sx={{ border: "2px dashed #e0e0e0", borderRadius: 2, p: 2, minWidth: 80, textAlign: "center", cursor: "pointer" }}
                                        onClick={() => document.getElementById("primary-image-input").click()}>
                                        <Add /><Typography variant="body2">{en_ja["Add Image"]}</Typography>
                                    </Box>
                                    <input type="file" id="primary-image-input" style={{ display: "none" }} accept="image/*"
                                        onChange={(e) => handleFileChange(e, "primary")} />
                                </Box>
                            </CardContent>

                            {/* Images */}
                            <CardHeader title={en_ja["Images"]} sx={{ pb: 0 }}
                                titleTypographyProps={{ variant: "subtitle1", sx: { fontWeight: 600 } }} />
                            <CardContent sx={{ pb: 0 }}>
                                <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
                                    {imagePreviews.map((src, i) => (
                                        <Box key={i} sx={{ position: "relative", display: "inline-block" }}>
                                            <img src={src} width={100} height={80} style={{ borderRadius: 4 }} alt="images" />
                                            <IconButton size="small" onClick={() => {
                                                setImages(prev => prev.filter((_, idx) => idx !== i));
                                                setImagePreviews(prev => prev.filter((_, idx) => idx !== i));
                                            }}
                                                sx={{
                                                    position: "absolute", top: -8, right: -8, backgroundColor: "white", boxShadow: 1,
                                                    "&:hover": {
                                                        backgroundColor: "#dc3545",
                                                        color: "#f5f5f5"
                                                    }
                                                }}>
                                                <CloseIcon fontSize="small" />
                                            </IconButton>
                                        </Box>
                                    ))}
                                    <Box sx={{ border: "2px dashed #e0e0e0", borderRadius: 2, p: 2, minWidth: 80, textAlign: "center", cursor: "pointer" }}
                                        onClick={() => document.getElementById("images-input").click()}>
                                        <Add /><Typography variant="body2">{en_ja["Add Images"]}</Typography>
                                    </Box>
                                    <input type="file" id="images-input" style={{ display: "none" }} multiple accept="image/*"
                                        onChange={(e) => handleFileChange(e, "images")} />
                                </Box>
                            </CardContent>

                            {/* Videos */}
                            <CardHeader title={en_ja["Videos"]} sx={{ pb: 0 }}
                                titleTypographyProps={{ variant: "subtitle1", sx: { fontWeight: 600 } }} />
                            <CardContent sx={{ pb: 0 }}>
                                <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
                                    {videoPreviews.map((src, i) => (
                                        <Box key={i} sx={{ position: "relative", display: "inline-block" }}>
                                            <video src={src} width={120} height={80} controls style={{ borderRadius: 4 }} />
                                            <IconButton size="small" onClick={() => {
                                                setVideos(prev => prev.filter((_, idx) => idx !== i));
                                                setVideoPreviews(prev => prev.filter((_, idx) => idx !== i));
                                            }}
                                                sx={{
                                                    position: "absolute", top: -8, right: -8, backgroundColor: "white", boxShadow: 1,
                                                    "&:hover": {
                                                        backgroundColor: "#dc3545",
                                                        color: "#f5f5f5"
                                                    }
                                                }}>
                                                <CloseIcon fontSize="small" />
                                            </IconButton>
                                        </Box>
                                    ))}
                                    <Box sx={{ border: "2px dashed #e0e0e0", borderRadius: 2, p: 2, minWidth: 80, textAlign: "center", cursor: "pointer" }}
                                        onClick={() => document.getElementById("videos-input").click()}>
                                        <Add /><Typography variant="body2">{en_ja["Add Videos"]}</Typography>
                                    </Box>
                                    <input type="file" id="videos-input" style={{ display: "none" }} multiple accept="video/*"
                                        onChange={(e) => handleFileChange(e, "videos")} />
                                </Box>
                            </CardContent>

                            <CardHeader title={en_ja["Manuals"]} sx={{ pb: 0 }}
                                titleTypographyProps={{ variant: "subtitle1", sx: { fontWeight: 600 } }} />
                            <CardContent sx={{ pb: 0 }}>
                                <Box sx={{ display: "flex", alignItems: "flex-start" }}>
                                    <Box sx={{ display: "flex", flexDirection: "column" }}>
                                        {manuals.map((item, i) => (
                                            <Box key={i} sx={{ display: "flex", alignItems: "center" }}>
                                                <a
                                                    href={item instanceof File ? URL.createObjectURL(item) : item}
                                                    download={getManualName(item)}
                                                    style={{ textDecoration: "underline", color: "blue" }}
                                                >
                                                    {getManualName(item)}
                                                </a>
                                                <IconButton size="small" onClick={() => {
                                                    setManuals(prev => prev.filter((_, idx) => idx !== i));
                                                }}
                                                    sx={{
                                                        "&:hover": {
                                                            backgroundColor: "#dc3545",
                                                            color: "#f5f5f5",
                                                        },
                                                        mx: 1, backgroundColor: "white", boxShadow: 1,
                                                    }}>
                                                    <CloseIcon fontSize="small" />
                                                </IconButton>
                                            </Box>
                                        ))}
                                    </Box>

                                    <Box
                                        sx={{
                                            border: "2px dashed #e0e0e0",
                                            borderRadius: 2,
                                            cursor: "pointer",
                                            p: 2,
                                            minWidth: 80,
                                            textAlign: "center"
                                        }}
                                        onClick={() => document.getElementById("manuals-input").click()}
                                    >
                                        <Add />
                                        <Typography variant="body2" textAlign="center">{en_ja["Add Manuals"]}</Typography>
                                    </Box>

                                    <input
                                        type="file"
                                        id="manuals-input"
                                        style={{ display: "none" }}
                                        multiple
                                        accept=".pdf,.doc,.docx"
                                        onChange={(e) => handleFileChange(e, "manuals")}
                                    />
                                </Box>
                            </CardContent>
                        </Card>
                    </Box>
                </Box>

                {/* FAQs */}
                <Card sx={{ mb: 2 }}>
                    <CardHeader title={en_ja["FAQs"]} sx={{ pb: 0 }} titleTypographyProps={{ variant: "subtitle1", sx: { fontWeight: 600 } }} />
                    <CardContent>
                        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
                            <TextField label={en_ja["Question"]} fullWidth value={faqQuestion} onChange={(e) => setFaqQuestion(e.target.value)} margin="small" />
                            <TextField label={en_ja["Answer"]} value={faqAnswer} onChange={(e) => setFaqAnswer(e.target.value)} fullWidth margin="normal" />
                            <Button variant="contained" startIcon={<Add />} onClick={handleAddFaq} sx={{ backgroundColor: "rgba(220, 53, 69, 1)" }}>
                                {en_ja["Add FAQ"]}
                            </Button>
                        </Box>
                        {faqs.map((faq, i) => (
                            <Box key={i} sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1, border: "1px solid #ccc", p: 1, borderRadius: 1 }}>
                                <Box sx={{ flex: 1, pr: 2 }}>
                                    <Typography variant="body2"><b>Q:</b> {faq.question}</Typography>
                                    <Typography variant="body2"><b>A:</b> {faq.answer}</Typography>
                                </Box>

                                <Box sx={{ display: "flex", gap: 1 }}>
                                    <IconButton aria-label="edit" size="small" onClick={() => handleEditFaq(i)}>
                                        <EditIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton
                                        aria-label="delete"
                                        size="small"
                                        onClick={() => handleDeleteFaq(i)}
                                        sx={{
                                            color: "error.main",
                                            "&:hover": { backgroundColor: "error.light", color: "error.dark" },
                                        }}
                                    >
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </Box>
                            </Box>
                        ))}
                    </CardContent>
                </Card>

                {/* Footer Actions */}
                <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 2 }}>
                    <Button variant="outlined" onClick={handleCloseDialog}>{en_ja["Cancel"]}</Button>
                    <Button variant="contained" color="primary" onClick={handleSubmit} sx={{ backgroundColor: "rgba(220, 53, 69, 1)" }}>
                        {editMode ? en_ja["Update Product"] : en_ja["Publish Product"]}
                    </Button>
                </Box>
            </DialogContent>
        </Dialog>
    );
};

export default AddProduct;
